import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { createJob, publishJobInternally, publishJobExternally } from "../../http";
import Loading from "../Loading";
import "react-toastify/dist/ReactToastify.css";

const JobCreation = () => {
  const { user } = useSelector(state => state.authSlice);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const initialState = {
    // BASIC JOB INFO
    title: "",
    role: "",
    department: "",
    location: "",
    employmentType: "full_time",
    
    // SALARY DETAILS
    salaryMin: "",
    salaryMax: "",
    salaryCurrency: "INR",
    salaryPeriod: "yearly",
    isSalaryVisibleToCandidates: true,
    
    // DESCRIPTION / JD
    description: "",
    responsibilities: "",
    requirements: "",
    
    // OWNER & META
    hiringManager: "",
    recruiter: "",
    headcount: 1,
    targetStartDate: "",
    
    // APPROVAL FLOW
    approver: "",
    approvalComment: "",
    
    // POSTING INFO
    isPublishedInternal: false,
    internalJobSlug: "",
    internalJobUrl: "",
    externalPostingLinks: [],
    
    // PUBLISH OPTIONS
    publishOption: "internal", // "internal", "external", or "both"
    externalSlug: ""
  };

  const [formData, setFormData] = useState(initialState);

  useEffect(() => {
    // Set current user as default hiring manager
    if (user?.id) {
      setFormData(prev => ({
        ...prev,
        hiringManager: user.id,
        createdBy: user.id
      }));
    }
  }, [user]);

  const inputEvent = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };




  const onSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    const requiredFields = ['title', 'role', 'location', 'description', 'hiringManager', 'approver'];
    const missingFields = requiredFields.filter(field => !formData[field]);
    
    if (missingFields.length > 0) {
      return toast.error(`Required fields missing: ${missingFields.join(', ')}`);
    }

    // Salary validation
    if (formData.salaryMin && formData.salaryMax && parseFloat(formData.salaryMax) < parseFloat(formData.salaryMin)) {
      return toast.error('Maximum salary must be greater than or equal to minimum salary');
    }

    setIsLoading(true);
    setError(null);

    try {
      // Prepare data according to API format
      const submissionData = {
        title: formData.title,
        role: formData.role,
        location: formData.location,
        salaryRange: {
          min: formData.salaryMin ? parseFloat(formData.salaryMin) : undefined,
          max: formData.salaryMax ? parseFloat(formData.salaryMax) : undefined,
          currency: formData.salaryCurrency
        },
        description: formData.description,
        hiringManager: formData.hiringManager,
        approver: formData.approver
      };

      // Add optional fields if they have values
      if (formData.department) submissionData.department = formData.department;
      if (formData.employmentType) submissionData.employmentType = formData.employmentType;
      if (formData.responsibilities) submissionData.responsibilities = formData.responsibilities;
      if (formData.requirements) submissionData.requirements = formData.requirements;
      if (formData.headcount && formData.headcount !== 1) submissionData.headcount = parseInt(formData.headcount);
      if (formData.targetStartDate) submissionData.targetStartDate = new Date(formData.targetStartDate);
      if (formData.approvalComment) submissionData.approvalComment = formData.approvalComment;
      if (formData.recruiter) submissionData.recruiter = formData.recruiter;

      // Add posting information if relevant
      const postingInfo = {};
      if (formData.isPublishedInternal) {
        postingInfo.isPublishedInternal = true;
        if (formData.internalJobSlug) postingInfo.internalJobSlug = formData.internalJobSlug;
        if (formData.internalJobUrl) postingInfo.internalJobUrl = formData.internalJobUrl;
      }
      
      const validExternalLinks = formData.externalPostingLinks.filter(link => link.platform && link.url);
      if (validExternalLinks.length > 0) {
        postingInfo.externalPostingLinks = validExternalLinks.map(link => ({
          platform: link.platform,
          url: link.url,
          postedAt: link.postedAt ? new Date(link.postedAt) : new Date()
        }));
      }
      
      if (Object.keys(postingInfo).length > 0) {
        submissionData.postingInfo = postingInfo;
      }

      console.log('Submitting job requisition:', submissionData);
      
      // Make actual API call
      const response = await createJob(submissionData);
      
      if (response.success || response._id) {
        const jobId = response._id;
        
        // Handle publishing based on user selection
        if (formData.publishOption === "internal" || formData.publishOption === "both") {
          try {
            await publishJobInternally(jobId);
            toast.success("Job published internally!");
          } catch (publishError) {
            console.error("Error publishing internally:", publishError);
            toast.warn("Job created but failed to publish internally");
          }
        }
        
        if ((formData.publishOption === "external" || formData.publishOption === "both") && formData.externalSlug) {
          try {
            await publishJobExternally(jobId, { externalSlug: formData.externalSlug });
            toast.success("Job published externally!");
          } catch (publishError) {
            console.error("Error publishing externally:", publishError);
            toast.warn("Job created but failed to publish externally");
          }
        }
        
        toast.success("Job requisition created successfully!");
        setFormData(initialState);
        
        // Reset hiring manager and created by after reset
        if (user?.id) {
          setFormData(prev => ({
            ...initialState,
            hiringManager: user.id,
            createdBy: user.id
          }));
        }
      } else {
        throw new Error(response.message || "Failed to create job requisition");
      }
      
    } catch (error) {
      console.error("Error creating job requisition:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to create job requisition";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading && !formData.title) {
    return <Loading />;
  }

  return (
    <div className="main-content">
      <section className="section">
        <div className="card">
          <div className="card-header">
            <h4>Create Job Requisition</h4>
          </div>
          <div className="card-body">
            {error && (
              <div className="alert alert-danger alert-dismissible fade show" role="alert">
                {error}
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setError(null)}
                  aria-label="Close"
                />
              </div>
            )}

            <form onSubmit={onSubmit} id="jobCreationForm">
              {/* Basic Job Information */}
              <div className="row mb-4">
                <div className="col-12">
                  <h5 className="section-title">Basic Job Information</h5>
                </div>
                
                <div className="form-group col-md-6">
                  <label>Job Title *</label>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <div className="input-group-text">
                        <i className="fas fa-briefcase"></i>
                      </div>
                    </div>
                    <input
                      onChange={inputEvent}
                      value={formData.title}
                      type="text"
                      name="title"
                      className="form-control"
                      required
                    />
                  </div>
                </div>

                <div className="form-group col-md-6">
                  <label>Role *</label>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <div className="input-group-text">
                        <i className="fas fa-user-tag"></i>
                      </div>
                    </div>
                    <input
                      onChange={inputEvent}
                      value={formData.role}
                      type="text"
                      name="role"
                      className="form-control"
                      required
                    />
                  </div>
                </div>

                <div className="form-group col-md-4">
                  <label>Department</label>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <div className="input-group-text">
                        <i className="fas fa-building"></i>
                      </div>
                    </div>
                    <input
                      onChange={inputEvent}
                      value={formData.department}
                      type="text"
                      name="department"
                      className="form-control"
                    />
                  </div>
                </div>

                <div className="form-group col-md-4">
                  <label>Location *</label>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <div className="input-group-text">
                        <i className="fas fa-map-marker-alt"></i>
                      </div>
                    </div>
                    <input
                      onChange={inputEvent}
                      value={formData.location}
                      type="text"
                      name="location"
                      className="form-control"
                      required
                    />
                  </div>
                </div>

                <div className="form-group col-md-4">
                  <label>Employment Type</label>
                  <select name="employmentType" onChange={inputEvent} value={formData.employmentType} className="form-control">
                    <option value="full_time">Full Time</option>
                    <option value="part_time">Part Time</option>
                    <option value="contract">Contract</option>
                    <option value="internship">Internship</option>
                    <option value="temporary">Temporary</option>
                    <option value="freelance">Freelance</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              {/* Salary Details */}
              <div className="row mb-4">
                <div className="col-12">
                  <h5 className="section-title">Salary Details</h5>
                </div>

                <div className="form-group col-md-3">
                  <label>Minimum Salary</label>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <div className="input-group-text">
                        <i className="fas fa-rupee-sign"></i>
                      </div>
                    </div>
                    <input
                      onChange={inputEvent}
                      value={formData.salaryMin}
                      type="number"
                      name="salaryMin"
                      className="form-control"
                      min="0"
                    />
                  </div>
                </div>

                <div className="form-group col-md-3">
                  <label>Maximum Salary</label>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <div className="input-group-text">
                        <i className="fas fa-rupee-sign"></i>
                      </div>
                    </div>
                    <input
                      onChange={inputEvent}
                      value={formData.salaryMax}
                      type="number"
                      name="salaryMax"
                      className="form-control"
                      min="0"
                    />
                  </div>
                </div>

                <div className="form-group col-md-3">
                  <label>Currency</label>
                  <select name="salaryCurrency" onChange={inputEvent} value={formData.salaryCurrency} className="form-control">
                    <option value="INR">INR</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>

                <div className="form-group col-md-3">
                  <label>Period</label>
                  <select name="salaryPeriod" onChange={inputEvent} value={formData.salaryPeriod} className="form-control">
                    <option value="yearly">Yearly</option>
                    <option value="monthly">Monthly</option>
                    <option value="weekly">Weekly</option>
                    <option value="daily">Daily</option>
                    <option value="hourly">Hourly</option>
                    <option value="fixed">Fixed</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-group col-md-12">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      name="isSalaryVisibleToCandidates"
                      checked={formData.isSalaryVisibleToCandidates}
                      onChange={inputEvent}
                      className="form-check-input"
                    />
                    <label className="form-check-label">
                      Show salary information to candidates
                    </label>
                  </div>
                </div>
              </div>

              {/* Job Description */}
              <div className="row mb-4">
                <div className="col-12">
                  <h5 className="section-title">Job Description</h5>
                </div>

                <div className="form-group col-md-12">
                  <label>Description *</label>
                  <textarea
                    onChange={inputEvent}
                    value={formData.description}
                    name="description"
                    className="form-control"
                    rows="4"
                    required
                  />
                </div>

                <div className="form-group col-md-12">
                  <label>Responsibilities</label>
                  <textarea
                    onChange={inputEvent}
                    value={formData.responsibilities}
                    name="responsibilities"
                    className="form-control"
                    rows="3"
                    placeholder="List key responsibilities (one per line or bullet points)"
                  />
                </div>

                <div className="form-group col-md-12">
                  <label>Requirements</label>
                  <textarea
                    onChange={inputEvent}
                    value={formData.requirements}
                    name="requirements"
                    className="form-control"
                    rows="3"
                    placeholder="List required qualifications, skills, and experience"
                  />
                </div>
              </div>

              {/* Owner & Meta Information */}
              <div className="row mb-4">
                <div className="col-12">
                  <h5 className="section-title">Owner & Meta Information</h5>
                </div>

                <div className="form-group col-md-4">
                  <label>Hiring Manager *</label>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <div className="input-group-text">
                        <i className="fas fa-user-tie"></i>
                      </div>
                    </div>
                    <input
                      onChange={inputEvent}
                      value={formData.hiringManager}
                      type="text"
                      name="hiringManager"
                      className="form-control"
                      readOnly
                      title="Auto-populated from current user"
                    />
                  </div>
                </div>

                <div className="form-group col-md-4">
                  <label>Recruiter</label>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <div className="input-group-text">
                        <i className="fas fa-user"></i>
                      </div>
                    </div>
                    <input
                      onChange={inputEvent}
                      value={formData.recruiter}
                      type="text"
                      name="recruiter"
                      className="form-control"
                      placeholder="User ID or leave empty"
                    />
                  </div>
                </div>

                <div className="form-group col-md-4">
                  <label>Headcount</label>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <div className="input-group-text">
                        <i className="fas fa-users"></i>
                      </div>
                    </div>
                    <input
                      onChange={inputEvent}
                      value={formData.headcount}
                      type="number"
                      name="headcount"
                      className="form-control"
                      min="1"
                    />
                  </div>
                </div>

                <div className="form-group col-md-6">
                  <label>Target Start Date</label>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <div className="input-group-text">
                        <i className="fas fa-calendar"></i>
                      </div>
                    </div>
                    <input
                      onChange={inputEvent}
                      value={formData.targetStartDate}
                      type="date"
                      name="targetStartDate"
                      className="form-control"
                    />
                  </div>
                </div>

                <div className="form-group col-md-6">
                  <label>Approver *</label>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <div className="input-group-text">
                        <i className="fas fa-user-check"></i>
                      </div>
                    </div>
                    <input
                      onChange={inputEvent}
                      value={formData.approver}
                      type="text"
                      name="approver"
                      className="form-control"
                      placeholder="Approver User ID"
                      required
                    />
                  </div>
                </div>

                <div className="form-group col-md-12">
                  <label>Approval Comment</label>
                  <textarea
                    onChange={inputEvent}
                    value={formData.approvalComment}
                    name="approvalComment"
                    className="form-control"
                    rows="2"
                    placeholder="Comments for the approver"
                  />
                </div>
              </div>

              {/* Publishing Options */}
              <div className="row mb-4">
                <div className="col-12">
                  <h5 className="section-title">Publishing Options</h5>
                </div>

                <div className="form-group col-md-12">
                  <label>Publish Job *</label>
                  <div className="mt-2">
                    <div className="form-check form-check-inline">
                      <input
                        type="radio"
                        name="publishOption"
                        value="internal"
                        checked={formData.publishOption === "internal"}
                        onChange={inputEvent}
                        className="form-check-input"
                      />
                      <label className="form-check-label">
                        Internally Only
                      </label>
                    </div>
                    <div className="form-check form-check-inline">
                      <input
                        type="radio"
                        name="publishOption"
                        value="external"
                        checked={formData.publishOption === "external"}
                        onChange={inputEvent}
                        className="form-check-input"
                      />
                      <label className="form-check-label">
                        Externally Only
                      </label>
                    </div>
                    <div className="form-check form-check-inline">
                      <input
                        type="radio"
                        name="publishOption"
                        value="both"
                        checked={formData.publishOption === "both"}
                        onChange={inputEvent}
                        className="form-check-input"
                      />
                      <label className="form-check-label">
                        Both Internal & External
                      </label>
                    </div>
                  </div>
                </div>

                {(formData.publishOption === "external" || formData.publishOption === "both") && (
                  <div className="form-group col-md-6">
                    <label>External Slug *</label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <div className="input-group-text">
                          <i className="fas fa-link"></i>
                        </div>
                      </div>
                      <input
                        onChange={inputEvent}
                        value={formData.externalSlug}
                        type="text"
                        name="externalSlug"
                        className="form-control"
                        placeholder="software-engineer-backend"
                        required={formData.publishOption === "external" || formData.publishOption === "both"}
                      />
                    </div>
                    <small className="text-muted">This will be used in the external job URL</small>
                  </div>
                )}
              </div>

              
              <div className="form-group text-center col-md-12">
                <button
                  className="btn btn-primary btn-lg"
                  type="submit"
                  disabled={isLoading}
                  style={{ width: '30vh' }}
                >
                  {isLoading ? "Creating..." : "Create Job"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default JobCreation;
